<template>
  <Layout title="Shipments">
    <div class="report-content">
      <div class="report-heading">
        <div>
          <h1>Shipments</h1>
          <p>Delivery performance, carrier usage and shipment activity.</p>
        </div>
      </div>
      <div class="report-filters">
        <DateRangePicker id="date-range" :value="dateRangeValue" @update:value="handleDateChanged" />
      </div>
      <b-row>
        <b-col>
          <HitMap :markers="mapMarkers" :stats="stats" :loading="statsLoading" :error="statsError" />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <CarriersTable
            :table-data="deliveryTime"
            title="Average Delivery Time"
            col-title="Days"
            block-id="DeliveryTime"
            @update:distribution-type-delivery-time="changeDistributionTypeDeliveryTime"
          />
        </b-col>
        <b-col lg="6">
          <CarriersTable
            :table-data="usage"
            title="Carrier Usage"
            col-title="Usage"
            block-id="Usage"
            @update:distribution-type-usage="changeDistributionTypeUsage"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <AveragePackagePrices
            :table-data="averagePrices"
            title="Average Package Prices"
            :filters="filters"
            @update:service-level="changeServiceLevel"
          />
        </b-col>
        <b-col lg="6">
          <PeriodChart :period-data="totalSales" title="Shipping Turnover" unit="kr" />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <PeriodChart :period-data="shipmentsCreated" title="Shipments Created" />
        </b-col>
        <b-col lg="6">
          <PeriodChart :period-data="shipmentsSent" title="Shipments Sent" />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <PeriodChart :period-data="shipmentsForwardToServicePoint" title="Shipments Forward to Service Point" />
        </b-col>
        <b-col lg="6">
          <PeriodChart :period-data="returnedShipments" title="Returned Shipments" />
        </b-col>
      </b-row>
    </div>
  </Layout>
</template>
<script lang="ts">
import type { ComponentData } from '@/typings/Shipments/ComponentData'
import type { DataCollection } from '@/typings/DataCollection'
import type { Periods } from '@/typings/Periods'
import type { PeriodsDiff } from '@/typings/Shipments/PeriodsDiff'
import type { MarkerItem } from '@/typings/Shipments/MarkerItem'
import type { Filter } from '@/typings/Filter'
import type { ShipmentStatisticItem, StatisticsBlockItem } from '@/typings/Shipments/ShipmentStatisticItem'
import { defineComponent, ref, watch, onMounted, type Ref } from 'vue'
import Layout from '@/views/dashboard/analytics-shell.vue'
import DateRangePicker from '@/views/dashboard/partials/report-period.vue'
import PeriodChart from '@/views/dashboard/partials/period-chart.vue'
import CarriersTable from '@/views/dashboard/partials/carrier-table-with-diff.vue'
import AveragePackagePrices from '@/views/dashboard/partials/average-package-prices.vue'
import HitMap from '@/views/dashboard/partials/map.vue'
import Shipments from '@/requests/Shipments'
import ErrorHandler from '@/requests/ErrorHandler'
import { withDeliveryAvailability } from '@/utils/delivery-availability'
import moment from 'moment/moment'

export default defineComponent<ComponentData>({
  components: {
    HitMap,
    PeriodChart,
    CarriersTable,
    DateRangePicker,
    Layout,
    AveragePackagePrices,
  },
  setup() {
    const shipmentsRequest = Shipments()

    const dateRangeValue: Ref<string> = ref(
      moment().subtract(29, 'days').format('YYYY-MM-DD') + ' - ' + moment().format('YYYY-MM-DD'),
    )

    const distributionTypeDeliveryTime: Ref<string> = ref('package')
    const distributionTypeUsage: Ref<string> = ref('package')
    const serviceLevel: Ref<string> = ref('priority')

    const createFilters = (): Filter => ({
      period: dateRangeValue.value,
      distributionChannel: null,
      serviceLevel: null,
    })

    const statsLoading = ref(true)
    const statsError = ref(false)
    let statsRequest = 0
    const stats: Ref<ShipmentStatisticItem[]> = ref([
      { icon: 'uil uil-window-restore float-end', number: '', title: 'Info Received', subtext: '' },
      { icon: 'mdi mdi-archive-check-outline float-end', number: '', title: 'Delivered', subtext: '' },
      { icon: 'mdi mdi-alarm float-end', number: '', title: 'In Transit', subtext: '' },
      { icon: 'uil uil-window-restore float-end', number: '', title: 'Out for Delivery', subtext: '' },
      { icon: 'mdi mdi-archive-check-outline float-end', number: '', title: 'Failed Attempt', subtext: '' },
      { icon: 'mdi mdi-alarm float-end', number: '', title: 'Exception', subtext: '' },
    ])
    const mapMarkers: Ref<MarkerItem[]> = ref([])
    const totalSales: Ref<Periods> = ref({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
    const shipmentsSent: Ref<Periods> = ref({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
    const shipmentsForwardToServicePoint: Ref<Periods> = ref({
      currentPeriod: [],
      previousPeriod: [],
      periodLabels: [],
    })
    const returnedShipments: Ref<Periods> = ref({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
    const shipmentsCreated: Ref<Periods> = ref({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
    const averagePrices: Ref<PeriodsDiff[]> = ref([])
    const deliveryTime: Ref<PeriodsDiff[]> = ref([])
    const usage: Ref<PeriodsDiff[]> = ref([])
    const filters: Ref<Filter> = ref(createFilters())

    const changeDistributionTypeDeliveryTime = (value: string) => {
      distributionTypeDeliveryTime.value = value
      getDeliveryTime({ ...createFilters(), distributionChannel: value })
    }

    const changeDistributionTypeUsage = (value: string) => {
      distributionTypeUsage.value = value
      getUsage({ ...createFilters(), distributionChannel: value })
    }

    const changeServiceLevel = (value: string) => {
      serviceLevel.value = value
      getAveragePackagePrices({ ...createFilters(), serviceLevel: value })
    }

    const handleDateChanged = (value: string) => {
      dateRangeValue.value = value
      filters.value.period = value
    }

    const getStatsData = (filters: Filter) => {
      const request = ++statsRequest
      statsLoading.value = true
      statsError.value = false
      shipmentsRequest
        .getStatsData(filters)
        .then((data: DataCollection<PeriodsDiff>) => {
          if (request !== statsRequest) return
          const defaultData: PeriodsDiff = { currentPeriod: '0', previousPeriod: '0', diff: '0', name: '' }
          let defStat: StatisticsBlockItem = {
            infoReceived: defaultData,
            delivered: defaultData,
            inTransit: defaultData,
            outForDelivery: defaultData,
            failedAttempt: defaultData,
            exception: defaultData,
          }
          Object.keys(defStat).forEach((key: string) => {
            defStat[key as keyof StatisticsBlockItem] =
              data.data.find((item: PeriodsDiff) => item.name === key) || defaultData
          })
          stats.value = [
            {
              icon: 'uil uil-window-restore float-end',
              number: defStat.infoReceived.currentPeriod,
              title: 'Info Received',
              subtext: '',
            },
            {
              icon: 'mdi mdi-archive-check-outline float-end',
              number: defStat.delivered.currentPeriod,
              title: 'Delivered',
              subtext: '',
            },
            {
              icon: 'mdi mdi-alarm float-end',
              number: defStat.inTransit.currentPeriod,
              title: 'In Transit',
              subtext: '',
            },
            {
              icon: 'uil uil-window-restore float-end',
              number: defStat.outForDelivery.currentPeriod,
              title: 'Out for Delivery',
              subtext: '',
            },
            {
              icon: 'mdi mdi-archive-check-outline float-end',
              number: defStat.failedAttempt.currentPeriod,
              title: 'Failed Attempt',
              subtext: defStat.failedAttempt.diff,
            },
            {
              icon: 'mdi mdi-alarm float-end',
              number: defStat.exception.currentPeriod,
              title: 'Exception',
              subtext: defStat.exception.diff,
            },
          ]
        })
        .catch((error) => {
          if (request !== statsRequest) return
          statsError.value = true
          ErrorHandler.handle(error)
        })
        .finally(() => {
          if (request === statsRequest) statsLoading.value = false
        })
    }

    const getCitiesData = (filters: Filter) => {
      shipmentsRequest
        .getCitiesData(filters)
        .then((data: DataCollection<MarkerItem>) => {
          mapMarkers.value = data.data
        })
        .catch(ErrorHandler.handle)
    }

    const getTotalSales = (filters: Filter) => {
      shipmentsRequest
        .getTotalSales(filters)
        .then((data: Periods) => {
          totalSales.value = data
        })
        .catch(ErrorHandler.handle)
    }

    const getShipmentsCreated = (filters: Filter) => {
      shipmentsRequest
        .getShipmentsCreated(filters)
        .then((data: Periods) => {
          shipmentsCreated.value = data
        })
        .catch(ErrorHandler.handle)
    }

    const getShipmentsSent = (filters: Filter) => {
      shipmentsRequest
        .getShipmentsSent(filters)
        .then((data: Periods) => {
          shipmentsSent.value = data
        })
        .catch(ErrorHandler.handle)
    }

    const getShipmentsForwardToServicePoint = (filters: Filter) => {
      shipmentsRequest
        .getShipmentsForwardToServicePoint(filters)
        .then((data: Periods) => {
          shipmentsForwardToServicePoint.value = data
        })
        .catch(ErrorHandler.handle)
    }

    const getReturnedShipments = (filters: Filter) => {
      shipmentsRequest
        .getReturnedShipments(filters)
        .then((data: Periods) => {
          returnedShipments.value = data
        })
        .catch(ErrorHandler.handle)
    }

    let deliveryRequest = 0
    const getDeliveryTime = (filters: Filter) => {
      const request = ++deliveryRequest
      deliveryTime.value = []
      Promise.all([
        shipmentsRequest.getDeliveryTime({ ...filters }),
        shipmentsRequest.getUsage({ ...filters }).catch(() => null),
      ])
        .then(([data, counts]) => {
          if (request !== deliveryRequest) return
          deliveryTime.value = counts ? withDeliveryAvailability(data.data, counts.data) : data.data
        })
        .catch(ErrorHandler.handle)
    }

    const getUsage = (filters: Filter) => {
      shipmentsRequest
        .getUsage(filters)
        .then((data: DataCollection<PeriodsDiff>) => {
          usage.value = data.data
        })
        .catch(ErrorHandler.handle)
    }

    const getAveragePackagePrices = (filters: Filter) => {
      shipmentsRequest
        .getAveragePackagePrice(filters)
        .then((data: DataCollection<PeriodsDiff>) => {
          averagePrices.value = data.data
        })
        .catch(ErrorHandler.handle)
    }

    const reloadData = () => {
      const filters = createFilters()
      getStatsData(filters)
      getCitiesData(filters)
      getTotalSales(filters)
      getShipmentsSent(filters)
      getShipmentsCreated(filters)
      getShipmentsForwardToServicePoint(filters)
      getReturnedShipments(filters)
      getDeliveryTime({ ...filters, distributionChannel: distributionTypeDeliveryTime.value })
      getUsage({ ...filters, distributionChannel: distributionTypeUsage.value })
      getAveragePackagePrices({ ...filters, serviceLevel: serviceLevel.value })
    }

    watch(dateRangeValue, reloadData)

    onMounted(reloadData)

    return {
      usage,
      stats,
      statsLoading,
      statsError,
      filters,
      mapMarkers,
      totalSales,
      deliveryTime,
      averagePrices,
      dateRangeValue,
      shipmentsSent,
      shipmentsCreated,
      shipmentsForwardToServicePoint,
      returnedShipments,
      changeDistributionTypeDeliveryTime,
      changeDistributionTypeUsage,
      changeServiceLevel,
      handleDateChanged,
    }
  },
})
</script>
